import { useEffect } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { useAdminStore } from '@/lib/stores/admin-store';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { AdminLayout } from './Layout';
import Overview from './Overview';
import { UsersPage } from './Users';
import { ExperiencesPage } from './Experiences';
import { PaymentsPage } from './Payments';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { AdminTransferApprovals } from '@/components/admin/AdminTransferApprovals';

/**
 * Main admin page component with routing to different admin pages
 */
export default function AdminPage() {
  const navigate = useNavigate();
  const { user, profile, isLoading: authLoading } = useUnifiedAuth();
  const { isInitialized: isAdminInitialized, error, initialize } = useAdminStore();

  // Handle auth checking and admin store initialization
  useEffect(() => {
    console.log("[AdminPage] Auth state:", {
      authLoading,
      userId: user?.id,
      userRole: profile?.role,
      isAdminInitialized
    });

    if (authLoading) {
      return; // Still initializing auth, wait.
    }

    if (!user) {
      console.log("[AdminPage] No user found, redirecting to login");
      navigate("/login");
      return;
    }

    // Use session user role first, fall back to profile role
    const userRole = user.user_metadata?.role || profile?.role;
    if (userRole !== 'admin') {
      console.warn("[AdminPage] User is not admin, redirecting. User role:", userRole);
      navigate("/");
      return;
    }

    if (userRole === 'admin' && !isAdminInitialized) {
      console.log("[AdminPage] Initializing admin store for admin user:", user.id);
      initialize().catch(err => {
        console.error("[AdminPage] Failed to initialize admin store:", err);
      });
    }
  }, [user, profile, authLoading, isAdminInitialized, initialize, navigate]);

  // Show spinner while auth is initializing
  if (authLoading) {
    return <LoadingSpinner />;
  }

  // Check for user and admin role - this is backup for the useEffect redirects
  const userRole = user?.user_metadata?.role || profile?.role;
  if (!user || userRole !== 'admin') {
    return null; // useEffect will handle the redirection
  }

  // Wait for admin store to initialize
  if (!isAdminInitialized) {
    console.log("[AdminPage] Waiting for admin store to initialize...");
    return <LoadingSpinner />;
  }

  // We show the error in the AdminOverviewPage now, not blocking the whole UI
  // if (error) {
  //   console.error("[AdminPage] Admin store error:", error);
  //   return <ErrorMessage message={error} />;
  // }

  return (
    <AdminLayout>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/experiences" element={<ExperiencesPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/transfers" element={<AdminTransferApprovals />} />
        {/* Activity log page will be added later */}
        <Route path="/activity" element={<Overview />} />
      </Routes>
    </AdminLayout>
  );
} 